/**
 * Centralized cache key registry and TTL constants.
 *
 * All IDB cache keys in the application MUST be produced by functions in this file.
 * This eliminates scattered hardcoded strings and ensures consistent naming.
 *
 * Key format: {domain}:{resource}:{scope}
 */

// ── TTL Constants (milliseconds) ────────────────────────────────

export const TTL = {
  /** Senders list — stable, refreshed hourly */
  SENDERS: 60 * 60 * 1000,
  /** Message templates — stable, refreshed hourly */
  TEMPLATES: 60 * 60 * 1000,
  /** Conversation list first page — revalidation is instant, long TTL for max cache utilization */
  CONVERSATIONS: 7 * 24 * 60 * 60 * 1000,
  /** Messages first page — revalidation is instant, long TTL for max cache utilization */
  MESSAGES: 7 * 24 * 60 * 60 * 1000,
  /** Chat labels — semi-static org data */
  CHAT_LABELS: 12 * 60 * 60 * 1000,
  /** Lookup settings — rarely changes */
  LOOKUP: 24 * 60 * 60 * 1000,
  /** Module fields — rarely changes */
  MODULE_FIELDS: 24 * 60 * 60 * 1000,
  /** Stale time — skip background revalidation if cache is younger than this */
  STALE_TIME: 2 * 60 * 1000,
} as const;

// ── Cache Key Builders ──────────────────────────────────────────

export const cacheKeys = {
  /** Senders list for an extension */
  senders: (ext: string): string =>
    `senders:${ext}`,

  /** Message templates for an extension + optional channel */
  templates: (ext: string, channel?: string): string =>
    `templates:${ext}:${channel || 'all'}`,

  /** Conversation list first page — includes filter hash for cache isolation */
  conversations: (ext: string, filters: Record<string, string | undefined>): string => {
    const parts = Object.entries(filters)
      .filter(([, v]) => v != null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`);
    return `conversations:${ext}:${parts.join('&') || 'default'}`;
  },

  /** Messages first page for a specific conversation */
  messages: (ext: string, convId: string): string =>
    `messages:${ext}:${convId}`,

  /** Lookup settings for a user */
  lookup: (ext: string, uid: string): string =>
    `lookup:${ext}:${uid}`,

  /** Chat labels for an org */
  chatLabels: (ext: string, orgId: string): string =>
    `labels:${ext}:${orgId}`,

  /** CRM module fields */
  moduleFields: (ext: string, module: string): string =>
    `fields:${ext}:${module}`,

  /** CRM contact search (searchContactMessages) */
  contactSearch: (ext: string, phone: string): string =>
    `contactSearch:${ext}:${phone}`,

  /**
   * Conversation-list record-enrichment lookup — caches the result of
   * `saasModule.searchAcrossModules(phone, 'phone', { modules })` so two
   * conversations sharing a phone (e.g. SMS + WhatsApp from the same
   * person) only hit the CRM once, and so a "no match" result survives
   * page refreshes for the TTL window.
   *
   * `modulesKey` is the resolved modules list joined with commas — the
   * cache invalidates naturally when the user changes their Inbox-Settings
   * module slots.
   */
  convEnrichment: (
    saasCode: string,
    orgId: string,
    phone: string,
    modulesKey: string
  ): string =>
    `convEnrich:${saasCode}:${orgId}:${phone}:${modulesKey}`,
};

/**
 * Shared prefix for `convEnrichment` keys — used by `clearByKeyPattern`
 * when the user changes their pinned enrichment modules so stale entries
 * under the old `modulesKey` don't linger until TTL expiry.
 */
export const CONV_ENRICHMENT_KEY_PREFIX = 'convEnrich:';
